import { Card, CardContent } from "@/components/ui/card";
import { FieldDisplay } from "@/components/ui/FieldDisplay";
import { SpeedFieldDisplay } from "@/components/UnitDetail/SpeedFieldDisplay";
import { EcmFieldDisplay } from "@/components/UnitDetail/EcmFieldDisplay";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import type { ModifiedUnitStats } from "@/lib/utils/unit-stat-modifiers";
import type { TerrainSpeed } from "@/lib/utils/terrain-speed-calculator";
import { MovementType } from "@/lib/types/unit";

/** Accordion `value`s for unit stat categories */
export const UNIT_STATS_ACCORDION_IDS = [
  "movement",
  "optics",
  "flying",
  "other",
] as const;

export const UNIT_STATS_ACCORDION_DEFAULT_OPEN: string[] = [...UNIT_STATS_ACCORDION_IDS];

interface UnitStatsProps {
  speed: number;
  roadSpeed: number;
  rotationTime: number;
  optics: number;
  airOptics: number;
  lowAltOptics?: number;
  opticsRange?: number;
  airOpticsRange?: number;
  lowAltOpticsRange?: number;
  stealth: number;
  ecm: number;
  fuel: number;
  fuelMove: number;
  supply: number;
  advancedDeployment: number;
  maxDamage: number;
  dangerousness?: number;
  agility?: number;
  flyingAltitude?: number;
  travelTime?: number | null;
  bombStrategy?: string;
  movementType: MovementType;
  modifiedStats?: ModifiedUnitStats;
  terrainSpeeds?: TerrainSpeed[];
  /** Use inside another Card to avoid nested cards */
  variant?: "card" | "embedded";
}

export function UnitStats({
  speed,
  roadSpeed,
  rotationTime,
  optics,
  airOptics,
  lowAltOptics,
  opticsRange,
  airOpticsRange,
  lowAltOpticsRange,
  stealth,
  ecm,
  fuel,
  fuelMove,
  supply,
  advancedDeployment,
  maxDamage,
  dangerousness,
  agility,
  flyingAltitude,
  travelTime,
  bombStrategy,
  movementType,
  modifiedStats,
  terrainSpeeds,
  variant = "card",
}: UnitStatsProps) {
  const movementFields = [
    roadSpeed > 0 && { 
      fieldKey: "roadSpeed", 
      value: modifiedStats?.roadSpeed.modified ?? roadSpeed,
      isModified: modifiedStats?.roadSpeed.isModified ?? false,
      baseValue: modifiedStats?.roadSpeed.base,
    },
    rotationTime > 0 && { 
      fieldKey: "rotationTime", 
      value: modifiedStats?.rotationTime.modified ?? rotationTime,
      isModified: modifiedStats?.rotationTime.isModified ?? false,
      baseValue: modifiedStats?.rotationTime.base,
    },
    { fieldKey: "fuel", value: fuel },
    fuelMove > 0 && { fieldKey: "fuelMove", value: fuelMove },
  ].filter(Boolean) as Array<{ fieldKey: string; value: unknown; isModified?: boolean; baseValue?: unknown }>;

  const opticsFields = [
    { 
      fieldKey: "optics", 
      value: modifiedStats?.optics.modified ?? optics,
      isModified: modifiedStats?.optics.isModified ?? false,
      baseValue: modifiedStats?.optics.base,
    },
    airOptics > 0 && { 
      fieldKey: "airOptics", 
      value: modifiedStats?.airOptics?.modified ?? airOptics,
      isModified: modifiedStats?.airOptics?.isModified ?? false,
      baseValue: modifiedStats?.airOptics?.base,
    },
    lowAltOptics && lowAltOptics > 0 && { 
      fieldKey: "lowAltOptics", 
      value: modifiedStats?.lowAltOptics?.modified ?? lowAltOptics,
      isModified: modifiedStats?.lowAltOptics?.isModified ?? false,
      baseValue: modifiedStats?.lowAltOptics?.base,
    },
    opticsRange && { fieldKey: "opticsRange", value: opticsRange },
    airOpticsRange && { fieldKey: "airOpticsRange", value: airOpticsRange },
  ].filter(Boolean) as Array<{ fieldKey: string; value: unknown; isModified?: boolean; baseValue?: unknown }>;

  const flyingFields = [
    flyingAltitude !== undefined && { fieldKey: "flyingAltitude", value: flyingAltitude },
    travelTime !== null && travelTime !== undefined && { fieldKey: "travelTime", value: travelTime },
    bombStrategy && { fieldKey: "bombStrategy", value: bombStrategy },
    agility !== undefined && { fieldKey: "agility", value: agility },
  ].filter(Boolean) as Array<{ fieldKey: string; value: unknown; isModified?: boolean; baseValue?: unknown }>;

  const otherFields = [
    { fieldKey: "supply", value: supply },
    advancedDeployment > 0 && { fieldKey: "advancedDeployment", value: advancedDeployment },
    { 
      fieldKey: "stealth", 
      value: modifiedStats?.stealth.modified ?? stealth,
      isModified: modifiedStats?.stealth.isModified ?? false,
      baseValue: modifiedStats?.stealth.base,
    },
    // Always render Dangerousness so the UI doesn't silently omit the stat when the value is missing.
    { fieldKey: "dangerousness", value: dangerousness },
  ].filter(Boolean) as Array<{ fieldKey: string; value: unknown; isModified?: boolean; baseValue?: unknown }>;

  // Determine if this should show the "Flying" card.
  // Helicopters can have ECM even when their NDF doesn't expose altitude/bomb-strategy fields.
  const showFlyingSection = movementType === MovementType.PLANE;

  const showEcmInOther =
    movementType === MovementType.PLANE || movementType === MovementType.HELICOPTER;

  const fieldLayout = variant === "embedded" ? "inline" : "stack";
  const fieldListGap = variant === "embedded" ? "space-y-0.5" : "space-y-1";

  const contentPad =
    variant === "embedded" ? "px-1.5 pb-2 pt-0" : "px-2 pb-2 pt-0";

  const accordionItemClass = "border-0 border-t border-border";
  const accordionTriggerClass =
    "py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:no-underline sm:py-2";

  const statsGrid = (
    <Accordion
      type="multiple"
      className="w-full min-w-0"
      defaultValue={UNIT_STATS_ACCORDION_DEFAULT_OPEN}
    >
      <AccordionItem value="movement" className={accordionItemClass}>
        <AccordionTrigger className={accordionTriggerClass}>Movement</AccordionTrigger>
        <AccordionContent className={contentPad}>
          <div className={fieldListGap}>
            <SpeedFieldDisplay
              speed={modifiedStats?.speed.modified ?? speed}
              isModified={modifiedStats?.speed.isModified ?? false}
              baseValue={modifiedStats?.speed.base}
              terrainSpeeds={terrainSpeeds}
              className="min-w-0"
              compact={variant === "embedded"}
            />
            {movementFields.map((field, idx) => (
              <FieldDisplay
                key={idx}
                fieldKey={field.fieldKey}
                value={field.value}
                fieldType="unit"
                className="min-w-0"
                isModified={field.isModified}
                baseValue={field.baseValue}
                layout={fieldLayout}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="optics" className={accordionItemClass}>
        <AccordionTrigger className={accordionTriggerClass}>Optics</AccordionTrigger>
        <AccordionContent className={contentPad}>
          <div className={fieldListGap}>
            {opticsFields.map((field, idx) => (
              <FieldDisplay
                key={idx}
                fieldKey={field.fieldKey}
                value={field.value}
                fieldType="unit"
                className="min-w-0"
                isModified={field.isModified}
                baseValue={field.baseValue}
                layout={fieldLayout}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>

      {showFlyingSection && (
        <AccordionItem value="flying" className={accordionItemClass}>
          <AccordionTrigger className={accordionTriggerClass}>Flying</AccordionTrigger>
          <AccordionContent className={contentPad}>
            <div className={fieldListGap}>
              {flyingFields.map((field, idx) => (
                <FieldDisplay
                  key={idx}
                  fieldKey={field.fieldKey}
                  value={field.value}
                  fieldType="unit"
                  className="min-w-0"
                  isModified={field.isModified}
                  baseValue={field.baseValue}
                  layout={fieldLayout}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {otherFields.length > 0 && (
        <AccordionItem value="other" className={accordionItemClass}>
          <AccordionTrigger className={accordionTriggerClass}>Other</AccordionTrigger>
          <AccordionContent className={contentPad}>
            <div className={fieldListGap}>
              {showEcmInOther && (
                <EcmFieldDisplay
                  ecm={modifiedStats?.ecm.modified ?? ecm}
                  dodgeBonus={modifiedStats?.dodgeBonus?.modified}
                  isEcmModified={modifiedStats?.ecm.isModified ?? false}
                  isDodgeModified={modifiedStats?.dodgeBonus?.isModified ?? false}
                  baseEcm={modifiedStats?.ecm.base}
                  baseDodge={modifiedStats?.dodgeBonus?.base}
                  className="min-w-0"
                  compact={variant === "embedded"}
                />
              )}
              {otherFields.map((field, idx) => (
                <FieldDisplay
                  key={idx}
                  fieldKey={field.fieldKey}
                  value={field.value}
                  fieldType="unit"
                  className="min-w-0"
                  isModified={field.isModified}
                  baseValue={field.baseValue}
                  layout={fieldLayout}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  );

  if (variant === "embedded") {
    return (
      <div className="min-w-0 border-t px-1 pb-2 pt-1 mt-1 overflow-hidden sm:mt-1.5 sm:px-1.5 sm:pt-1.5">
        {statsGrid}
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-2 pt-0 overflow-hidden">{statsGrid}</CardContent>
    </Card>
  );
}

